import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initGa4, trackEvent, trackPageView } from './ga4';

const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;

function getScrollPercent(): number {
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop || 0;
  const scrollHeight = doc.scrollHeight || 0;
  const clientHeight = doc.clientHeight || window.innerHeight || 0;
  const scrollable = Math.max(1, scrollHeight - clientHeight);
  return Math.min(100, Math.max(0, (scrollTop / scrollable) * 100));
}

function getClosestTrackableElement(target: EventTarget | null): HTMLElement | null {
  let el = target as HTMLElement | null;
  while (el && el !== document.body) {
    if (el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement) return el;
    if (el.dataset && (el.dataset.gaEvent || el.dataset.gaLabel)) return el;
    el = el.parentElement;
  }
  return null;
}

function safeText(s: string | null | undefined, maxLen: number): string | undefined {
  if (!s) return undefined;
  const trimmed = s.trim();
  if (!trimmed) return undefined;
  return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
}

export default function AnalyticsTracker() {
  const location = useLocation();
  const pagePathWithSearch = useMemo(
    () => `${location.pathname}${location.search || ''}`,
    [location.pathname, location.search]
  );

  const scrollReachedRef = useRef<Set<number>>(new Set());
  const rafRef = useRef<number | null>(null);

  // Init once (StrictMode-safe).
  useEffect(() => {
    initGa4();
  }, []);

  // Page views + reset per-page scroll checkpoints.
  useEffect(() => {
    scrollReachedRef.current = new Set();
    trackPageView(pagePathWithSearch);
  }, [pagePathWithSearch]);

  // Scroll depth (25/50/75/100).
  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        const percent = getScrollPercent();

        for (const threshold of SCROLL_THRESHOLDS) {
          if (percent >= threshold && !scrollReachedRef.current.has(threshold)) {
            scrollReachedRef.current.add(threshold);
            trackEvent('scroll_depth', {
              percent: threshold,
              page_path: pagePathWithSearch,
            });
          }
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Catch initial (e.g. short pages or restore)
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [pagePathWithSearch]);

  // Clicks (CTA + outbound links).
  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      const el = getClosestTrackableElement(e.target);
      if (!el) return;

      const gaEvent = el.dataset.gaEvent || 'cta_click';
      const gaLabel =
        el.dataset.gaLabel ||
        (el instanceof HTMLAnchorElement ? el.getAttribute('href') : undefined) ||
        el.id ||
        safeText(el.getAttribute('aria-label'), 120) ||
        safeText(el.textContent, 80);

      // Anchor details (outbound vs internal)
      let linkUrl: string | undefined;
      let isOutbound: boolean | undefined;
      if (el instanceof HTMLAnchorElement && el.href) {
        linkUrl = el.href;
        try {
          isOutbound = new URL(el.href).origin !== window.location.origin;
        } catch {
          isOutbound = undefined;
        }
      }

      trackEvent(gaEvent, {
        page_path: pagePathWithSearch,
        label: gaLabel,
        link_url: linkUrl,
        is_outbound: isOutbound,
      });
    };

    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [pagePathWithSearch]);

  return null;
}

